import { Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, IconButton, Modal, Paper, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useInvitation } from "../../context/InvitationContext";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";
import { GradientTypography } from "../../utils/gradientTypography";
import { RowBox, RowCenteredBox } from "../../utils/styledBox";
import { useAlert } from "../../context/AlertContext";

const OrganizationInvitationModal: React.FC = () => {
  const { showModal, currentInvitation, closeModal, acceptInvitation, declineInvitation } = useInvitation();
  const navigate = useCustomNavigate();
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  if (!currentInvitation) {
    return null;
  }

  const handleAccept = async (organizationName) => {
    const success = await acceptInvitation(currentInvitation.uuid);
    if (success) {
      setAlert("success", t("preferences.invitation.acceptedSuccess", { organization: organizationName }));
      navigate("/preferences/enterprise");
    } else {
      setAlert("error", t("preferences.invitation.acceptError"));
    }
  };

  const handleDecline = async () => {
    await declineInvitation(currentInvitation.uuid);
  };

  // ロール名の翻訳
  const getRoleName = (role: string) => {
    switch (role) {
      case "owner":
        return t("preferences.enterprise.role.owner");
      case "manager":
        return t("preferences.enterprise.role.manager");
      case "member":
        return t("preferences.enterprise.role.member");
      default:
        return role;
    }
  };

  return (
    <Modal
      open={showModal}
      onClose={closeModal}
      aria-labelledby="invitation-modal-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        sx={{
          position: "relative",
          width: "90%",
          maxWidth: 600,
          maxHeight: "90vh",
          pt: 4,
          pb: 2,
          bgcolor: (theme) => theme.palette.background.paper + "D9",
          backdropFilter: "blur(5px)",
        }}
      >
        <RowBox gap={3} sx={{ px: 3.5 }} alignItems="center">
          <GradientTypography variant="h5" component="h2" id="invitation-modal-title" gutterBottom>
            {t("preferences.enterprise.invitation.title")}
          </GradientTypography>
        </RowBox>
        <Box sx={{ height: 1, overflow: "auto", maxHeight: "calc(90vh - 200px)", mb: 2 }}>
          <IconButton
            onClick={closeModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ mb: 2, overflow: "auto", p: 3 }}>
            <Typography variant="body1" paragraph>
              {t("preferences.enterprise.invitation.message", {
                organization: currentInvitation.organization.name,
                role: getRoleName(currentInvitation.role),
                inviter: currentInvitation.invited_by?.username,
              })}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {t("preferences.enterprise.invitation.expires_at", {
                date: new Date(currentInvitation.expires_at).toLocaleDateString(),
              })}
            </Typography>
          </Box>
        </Box>
        <RowCenteredBox gap={2}>
          <Button variant="outlined" onClick={handleDecline} sx={{ minWidth: 120 }} disableElevation>
            {t("preferences.enterprise.invitation.decline")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleAccept(currentInvitation.organization.name)}
            sx={{ minWidth: 120 }}
            disableElevation
          >
            {t("preferences.enterprise.invitation.accept")}
          </Button>
        </RowCenteredBox>
      </Paper>
    </Modal>
  );
};

export default OrganizationInvitationModal;
